import React, { useState, useRef, useEffect } from 'react';
import { useCurrency } from '../context/CurrencyContext';
import { resolveImageUrl } from '../utils/api';

const ProductCard = ({ product, onAddToCart }) => {
    const { currency, convertPrice, getCurrencySymbol } = useCurrency();
    const images = (product.images && product.images.length > 0) ? product.images : [product.image].filter(Boolean);
    const [activeIdx, setActiveIdx] = useState(0);
    // Once the shopper browses deliberately, stop the hover preview from
    // yanking the image back — that fight is what made browsing feel broken.
    const [browsed, setBrowsed] = useState(false);
    const hasMultiple = images.length > 1;

    // Products may carry optional variants (size, colour…), each with its own
    // price and stock. With variants, everything below reads from the selected
    // one rather than the product's own summary fields.
    const variants = product.variants || [];
    const hasVariants = variants.length > 0;
    const [variantId, setVariantId] = useState(() => {
        const firstAvailable = variants.find(v => v.stock > 0);
        return (firstAvailable || variants[0])?._id || '';
    });
    const selectedVariant = hasVariants
        ? variants.find(v => v._id === variantId) || variants[0]
        : null;

    const unitPrice = selectedVariant ? selectedVariant.price : product.price;
    const availableStock = selectedVariant ? selectedVariant.stock : product.stock;

    const inStock = availableStock > 0;
    const maxQty = inStock ? availableStock : 1;
    const [quantity, setQuantity] = useState(1);

    const clampQty = (value) => Math.min(maxQty, Math.max(1, value));

    const stepQty = (e, delta) => {
        e.preventDefault();
        e.stopPropagation();
        setQuantity(q => clampQty(q + delta));
    };

    const handleQtyInput = (e) => {
        const parsed = parseInt(e.target.value, 10);
        // Allow the field to be briefly empty while typing rather than
        // snapping back to 1 on every keystroke.
        setQuantity(Number.isNaN(parsed) ? '' : clampQty(parsed));
    };

    // 'idle' | 'adding' | 'added' | 'error'
    const [status, setStatus] = useState('idle');
    const resetTimer = useRef(null);

    useEffect(() => () => clearTimeout(resetTimer.current), []);

    const addToCart = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (status === 'adding') return; // ignore double-clicks mid-flight

        const qty = clampQty(parseInt(quantity, 10) || 1);
        setStatus('adding');

        const ok = await onAddToCart(product._id, qty, selectedVariant?._id || null);

        // `onAddToCart` may be a plain handler that returns nothing; treat only
        // an explicit `false` as failure so the card still works without it.
        setStatus(ok === false ? 'error' : 'added');
        if (ok !== false) setQuantity(1);

        clearTimeout(resetTimer.current);
        resetTimer.current = setTimeout(() => setStatus('idle'), 1600);
    };

    const goTo = (e, index) => {
        e.preventDefault();
        e.stopPropagation();
        setBrowsed(true);
        setActiveIdx((index + images.length) % images.length);
    };

    const getProductBadge = () => {
        if (product.isBestseller) return { text: 'BESTSELLER', cls: 'bg-blue-600 text-white' };
        if (product.discount) return { text: `${product.discount}% OFF`, cls: 'bg-emerald-500 text-white' };
        if (product.isNew) return { text: 'NEW', cls: 'bg-indigo-600 text-white' };
        return null;
    };

    const badge = getProductBadge();

    return (
        <div className="group bg-white rounded-3xl overflow-hidden border border-gray-100 hover:border-blue-200 transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl hover:shadow-blue-100">
            {/* Image area */}
            <div className="relative overflow-hidden bg-gray-50 aspect-[4/3]"
                onMouseEnter={() => { if (hasMultiple && !browsed) setActiveIdx(1); }}
                onMouseLeave={() => { if (!browsed) setActiveIdx(0); }}>
                <img
                    src={resolveImageUrl(images[activeIdx] || images[0])}
                    alt={`${product.name}${hasMultiple ? ` (${activeIdx + 1} of ${images.length})` : ''}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-all duration-700"
                />

                {hasMultiple && (
                    <>
                        {/* Arrows sit mid-height so the quick-add overlay, which
                            slides up from the bottom on hover, cannot cover them. */}
                        <button
                            type="button"
                            onClick={e => goTo(e, activeIdx - 1)}
                            aria-label="Previous image"
                            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-white/90 text-gray-700 shadow-sm opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-blue-500 max-md:opacity-100 hover:bg-white transition-opacity duration-200"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            onClick={e => goTo(e, activeIdx + 1)}
                            aria-label="Next image"
                            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-white/90 text-gray-700 shadow-sm opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-blue-500 max-md:opacity-100 hover:bg-white transition-opacity duration-200"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>

                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex gap-1">
                            {images.map((_, i) => (
                                <button key={i}
                                    type="button"
                                    onClick={e => goTo(e, i)}
                                    aria-label={`Show image ${i + 1}`}
                                    aria-current={i === activeIdx}
                                    className="p-1.5 -m-0.5 focus:outline-none group/dot"
                                >
                                    <span className={`block rounded-full transition-all duration-200 ${i === activeIdx ? 'bg-white w-4 h-1.5' : 'bg-white/60 group-hover/dot:bg-white w-1.5 h-1.5'} shadow-sm`} />
                                </button>
                            ))}
                        </div>
                    </>
                )}

                {/* Badge */}
                {badge && (
                    <div className={`absolute top-3 left-3 ${badge.cls} px-2.5 py-1 text-xs font-medium tracking-wide rounded-full shadow-lg`}>
                        {badge.text}
                    </div>
                )}

                {/* Wishlist */}
                <button
                    className="absolute top-3 right-3 p-2.5 bg-white/90 backdrop-blur-sm rounded-full border border-gray-100 hover:border-red-300 hover:bg-red-50 text-gray-400 hover:text-red-400 transition-all duration-300 opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 shadow-sm"
                    aria-label="Add to wishlist"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                </button>

            </div>

            {/* Content */}
            <div className="p-5">
                <h3 className="text-base font-medium text-gray-900 mb-1.5 tracking-tight line-clamp-1">{product.name}</h3>
                <p className="text-gray-500 text-sm mb-4 font-light line-clamp-2 leading-relaxed">{product.description}</p>

                {hasVariants && (
                    <div className="mb-4" role="group" aria-label="Choose an option">
                        <div className="flex flex-wrap gap-2">
                            {variants.map(v => {
                                const selected = v._id === selectedVariant?._id;
                                const soldOut = v.stock <= 0;
                                return (
                                    <button
                                        key={v._id}
                                        type="button"
                                        onClick={() => { setVariantId(v._id); setQuantity(1); }}
                                        disabled={soldOut}
                                        aria-pressed={selected}
                                        title={soldOut ? `${v.label} — out of stock` : v.label}
                                        className={`px-3 py-1.5 rounded-xl border text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                                            soldOut
                                                ? 'border-gray-200 text-gray-300 line-through cursor-not-allowed'
                                                : selected
                                                    ? 'border-blue-600 bg-blue-600 text-white shadow-sm shadow-blue-600/30'
                                                    : 'border-gray-200 text-gray-700 hover:border-gray-400'
                                        }`}
                                    >
                                        {v.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="flex items-center justify-between gap-3 mb-4">
                    <div className="flex items-baseline gap-2">
                        <span className="text-xl font-semibold text-gray-900">{getCurrencySymbol()}{convertPrice(unitPrice, currency)}</span>
                        {product.originalPrice && (
                            <span className="text-sm text-gray-400 line-through font-light">{getCurrencySymbol()}{convertPrice(product.originalPrice, currency)}</span>
                        )}
                    </div>

                    {/* Quantity — lives here rather than in the hover overlay so
                        touch devices, which never hover, can still reach it. */}
                    <div className="flex items-center border border-gray-200 rounded-full overflow-hidden shrink-0">
                        <button
                            type="button"
                            onClick={e => stepQty(e, -1)}
                            disabled={!inStock || quantity <= 1}
                            aria-label="Decrease quantity"
                            className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent transition-colors"
                        >
                            −
                        </button>
                        <input
                            type="text"
                            inputMode="numeric"
                            value={quantity}
                            onChange={handleQtyInput}
                            onBlur={() => setQuantity(q => clampQty(parseInt(q, 10) || 1))}
                            disabled={!inStock}
                            aria-label={`Quantity${inStock ? `, up to ${maxQty}` : ''}`}
                            className="w-9 text-center text-sm font-medium text-gray-900 focus:outline-none disabled:bg-gray-50"
                        />
                        <button
                            type="button"
                            onClick={e => stepQty(e, 1)}
                            disabled={!inStock || quantity >= maxQty}
                            aria-label="Increase quantity"
                            className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent transition-colors"
                        >
                            +
                        </button>
                    </div>
                </div>

                <p className="text-xs text-gray-400 font-light mb-3">
                    {inStock ? `${availableStock} in stock` : 'Out of stock'}
                </p>

                {/* The only add-to-cart control: always visible, so it works on
                    touch devices where the old hover overlay was unreachable. */}
                <button
                    onClick={addToCart}
                    disabled={!inStock || status === 'adding'}
                    aria-live="polite"
                    className={`w-full py-3 rounded-2xl font-semibold text-sm transition-colors duration-300 shadow-lg flex items-center justify-center gap-2 text-white disabled:cursor-not-allowed ${
                        status === 'added'
                            ? 'bg-emerald-500 shadow-emerald-500/30'
                            : status === 'error'
                                ? 'bg-red-500 shadow-red-500/30'
                                : 'bg-blue-600 hover:bg-blue-500 shadow-blue-600/30 disabled:bg-gray-300 disabled:shadow-none'
                    }`}
                >
                    {status === 'adding' ? (
                        <>
                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                            </svg>
                            Adding…
                        </>
                    ) : status === 'added' ? (
                        <span className="flex items-center gap-2 animate-pop-in">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                            Added to Cart
                        </span>
                    ) : status === 'error' ? (
                        <span className="animate-pop-in">Could not add</span>
                    ) : (
                        <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
                            </svg>
                            {inStock ? `Add ${quantity || 1} to Cart` : 'Out of stock'}
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default ProductCard;
